import React, { useState, useCallback, useEffect } from 'react';
import { Box, useMantineTheme, Badge } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { UserSection, StatusSection, TimeSection, SecretModal } from './components';
import { StatusMessage, UserInfo } from './types';
import classNames from 'classnames';
import styles from './StatusBar.module.scss';
import { isDebugModeActive, toggleDebugMode } from '@services/api/axios-instance';
import { IconBug } from '@tabler/icons-react';

interface StatusBarProps {
  status?: StatusMessage;
}

export const StatusBar: React.FC<StatusBarProps> = ({ status }) => {
  const theme = useMantineTheme();
  const isDark = theme.colors.dark != null;
  const isMobile = useMediaQuery('(max-width: 768px)');
  // Estado para el modal secreto
  const [, setClickCount] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDebugMode, setIsDebugMode] = useState(false);
  const [user, setUser] = useState<UserInfo | undefined>(undefined);

  // Verificar si el modo debug está activo
  useEffect(() => {
    const checkDebugMode = () => {
      const hasToken = !!localStorage.getItem('token');

      if (hasToken) {
        if (!isDebugMode) {
          toggleDebugMode();
        }

        try {
          // Obtener el token
          const token = localStorage.getItem('token') || '';
          // Extraer la parte del payload (segunda parte del token JWT)
          const payload = token.split('.')[1];
          // Decodificar el payload
          const decodedPayload = window.atob(payload);
          // Parsear el JSON
          const userData = JSON.parse(decodedPayload);
          setUser(userData);
        } catch (error) {
          console.error('Error al decodificar el token:', error);
        }
      }

      setIsDebugMode(isDebugModeActive());
    };

    checkDebugMode();
    // Verificar cada 5 segundos
    const interval = window.setInterval(checkDebugMode, 5000);

    return () => window.clearInterval(interval);
  }, []);

  // Función para manejar clics en la barra de estado
  const handleStatusBarClick = useCallback(() => {
    setClickCount(prev => {
      const newCount = prev + 1;
      if (newCount >= 5) {
        setIsModalOpen(true);
        return 0; // Reiniciar contador
      }
      return newCount;
    });
  }, []);

  // Función para cerrar el modal
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  return (
    <Box component="footer" className={styles.statusBar__footer}>
      <div
        className={styles.container}
        onClick={handleStatusBarClick}
        style={{ cursor: 'pointer' }}
      >
        <div
          className={classNames(styles.section, styles['section--left'], {
            [styles.dark]: isDark,
          })}
        >
          {user && <UserSection user={user} />}
          {isDebugMode && (
            <Badge
              color="blue"
              variant="filled"
              size="sm"
              leftSection={<IconBug size={12} />}
              style={{ marginLeft: '8px' }}
            >
              Debug
            </Badge>
          )}
        </div>
        {(!isMobile || status) && (
          <div className={classNames(styles.section, styles['section--center'])}>
            <StatusSection status={status} />
          </div>
        )}
        {!isMobile && (
          <div
            className={classNames(styles.section, styles['section--right'], {
              [styles.dark]: isDark,
            })}
          >
            <TimeSection />
          </div>
        )}
      </div>

      {/* Modal secreto */}
      <SecretModal opened={isModalOpen} onClose={handleCloseModal} />
    </Box>
  );
};
